/**
 * Timeline Slideout Panel Manager
 * Handles slideout panel functionality for timeline items
 * Updated for Now.js framework integration
 */
class TimelineSlideout {
  constructor() {
  }

  _t(key) {
    if (window.Now && typeof Now.translate === 'function') {
      return Now.translate(key);
    }
    if (window.I18nManager && typeof I18nManager.translate === 'function') {
      return I18nManager.translate(key);
    }
    return key;
  }

  _runInlineScripts(container, context = 'content') {
    if (!container) return;

    container.querySelectorAll('script').forEach((script) => {
      const code = (script.textContent || '').trim();
      // Prevent duplicate execution on re-render.
      if (script.parentNode) script.parentNode.removeChild(script);

      if (!code) return;

      try {
        // Execute in an IIFE scope so legacy bodies containing `return` are valid.
        const fn = new Function(`(function(){\n${code}\n}).call(window);`);
        fn.call(window);
      } catch (error) {
        // Legacy slideout snippets may contain non-standalone script fragments.
        // Skip them silently to avoid runtime crashes/noise.
      }
    });
  }

  async _sendFormRequest(url, params, callback) {
    if (window.ApiService) {
      try {
        const data = {};
        params.split('&').forEach(p => {
          const [k, v] = p.split('=');
          if (k) data[decodeURIComponent(k)] = decodeURIComponent(v || '');
        });
        const httpResult = await ApiService.post(url, data);
        // Unwrap HttpClient layer → leave Gcms\Api JSON for callback
        const result = (httpResult && httpResult.data !== undefined) ? httpResult.data : httpResult;
        if (callback) callback({responseText: JSON.stringify(result), responseJSON: result});
      } catch (e) {
        console.error('Request failed:', e);
      }
    } else if (window.send) {
      send(url, params, callback);
    }
  }

  /**
   * Turn freshly injected markup into live Now.js controls.
   *
   * Injected HTML is not observed, so the managers have to be pointed at the
   * container by hand — this is what Modal does for its own body. Without it a
   * server-rendered panel keeps native inputs instead of the Now.js date,
   * colour, range and icon-picker components.
   *
   * @param {Element} container
   */
  _enhance(container) {
    if (!container) return;
    try {
      const componentManager = window.ComponentManager || window.Now?.getManager?.('component');
      const elementManager = window.ElementManager || window.Now?.getManager?.('element');
      const formManager = window.FormManager || window.Now?.getManager?.('form');
      const i18n = window.I18nManager || window.Now?.getManager?.('i18n');

      if (componentManager && typeof componentManager.initializeExistingElements === 'function') {
        componentManager.initializeExistingElements('icon-picker');
      }
      if (elementManager && typeof elementManager.scan === 'function') {
        elementManager.scan(container);
      }
      if (formManager && typeof formManager.scan === 'function') {
        formManager.scan(container);
      }
      if (i18n && typeof i18n.updateElements === 'function') {
        i18n.updateElements(container);
      }
    } catch (err) {
      console.warn('TimelineSlideout: failed to enhance panel content', err);
    }
  }

  setupEventListeners() {
    const slideout = document.getElementById('timeline-slideout');
    const overlay = document.getElementById('timeline-slideout-overlay');

    if (!slideout || !overlay) return;

    // Close button
    const closeBtn = document.getElementById('slideout-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.hide());
    }

    // Overlay click to close
    overlay.addEventListener('click', () => this.hide());
    const tabs = slideout.querySelectorAll('.slideout-tab');
    tabs.forEach(tab => {
      tab.addEventListener('click', (e) => {
        const tabName = e.target.getAttribute('data-tab');
        this.switchTab(tabName);
      });
    });

    // Action buttons
    const cancelBtn = document.getElementById('slideout-cancel');
    const deleteBtn = document.getElementById('slideout-delete');

    if (cancelBtn) {
      cancelBtn.addEventListener('click', () => this.hide());
    }

    if (deleteBtn) {
      deleteBtn.addEventListener('click', () => this.delete());
    }

    // Save the slideout form (block / marker details) via ApiService.
    const elementForm = document.getElementById('timeline_element_form');
    if (elementForm) {
      elementForm.addEventListener('submit', (e) => {
        e.preventDefault();
        this.submitForm(elementForm);
      });
    }

    // Prevent slideout content clicks from closing the panel
    const slideoutContent = slideout.querySelector('.slideout-content');
    if (slideoutContent) {
      slideoutContent.addEventListener('click', (e) => {
        e.stopPropagation();
      });
    }

    const durationEl = document.getElementById('duration');
    if (durationEl) {
      durationEl.addEventListener('input', (e) => {
        const value = e.target.value;
        const durationValueEl = document.getElementById('duration_value');
        if (durationValueEl) durationValueEl.textContent = value;
      });
    }

    const addSubtaskBtn = document.getElementById('add-subtask');
    if (addSubtaskBtn) {
      addSubtaskBtn.addEventListener('click', () => {
        const projectId = document.getElementById('timeline_project_id');
        const blockId = document.getElementById('timeline_id');
        if (!projectId || !blockId) return;
        let q = 'action=edit&id=0';
        q += '&project_id=' + projectId.value;
        q += '&block_id=' + blockId.value;
        this._sendFormRequest('api/project/subtask/action', q, (xhr) => {
          let ret;
          try {ret = typeof xhr.responseJSON !== 'undefined' ? xhr.responseJSON : JSON.parse(xhr.responseText);} catch (e) {ret = null;}
          const payload = ret && ret.data ? ret.data : ret;
          if (payload && payload.content) {
            this._showFormModal(payload.content);
          }
        });
      });
    }

    slideout.querySelectorAll('[name^="subtasks_"]').forEach((input) => {
      input.addEventListener('click', () => {
        const action = input.name.replace('subtasks_', '');
        if (action === 'delete' && !confirm(this._t('You want to XXX ?').replace(/XXX/, this._t('delete')))) {
          return;
        }
        const id = input.parentNode.parentNode.id.replace('subtasks_', '');
        if (!id) return;
        let q = `action=${action}&id=${id}`;
        const projectId = document.getElementById('timeline_project_id');
        const blockId = document.getElementById('timeline_id');
        if (projectId) q += '&project_id=' + projectId.value;
        if (blockId) q += '&block_id=' + blockId.value;
        if (input.type === 'checkbox') q += '&value=' + (input.checked ? 1 : 0);
        this._sendFormRequest('api/project/subtask/action', q, (xhr) => {
          let ret;
          try {ret = typeof xhr.responseJSON !== 'undefined' ? xhr.responseJSON : JSON.parse(xhr.responseText);} catch (e) {ret = null;}
          const payload = ret && ret.data ? ret.data : ret;
          if (!payload) return;
          if (payload.remove_id) {
            const el = document.getElementById(payload.remove_id);
            if (el && el.parentNode) el.parentNode.removeChild(el);
          }
          if (payload.progress !== undefined && typeof window.updateBlock === 'function') {
            window.updateBlock({id: parseInt(payload.block_id), progress: payload.progress}, false);
          }
          if (payload.content) {
            this._showFormModal(payload.content);
          }
        });
      });
    });
  }


  show(itemData = null, projectId, mode = 'block') {
    const slideout = document.getElementById('timeline-slideout');
    const overlay = document.getElementById('timeline-slideout-overlay');

    if (!slideout || !overlay) return;

    this.mode = mode;

    const q = `action=${mode}&id=${itemData.id}&project_id=${projectId}`;
    this._sendFormRequest('api/project/timelineaction/action', q, (xhr) => {
      let ret;
      try {
        ret = typeof xhr.responseJSON !== 'undefined' ? xhr.responseJSON : JSON.parse(xhr.responseText);
      } catch (e) {ret = null;}
      // Gcms\Api wraps the payload as { success, message, data: { content, icon } }
      const payload = ret && ret.data ? ret.data : ret;
      if (payload && payload.content) {
        slideout.innerHTML = payload.content;
        this._runInlineScripts(slideout, 'slideout');
        // The icon picker is now the shared data-component="icon-picker",
        // rendered with its selection already applied server side.
        this._enhance(slideout);
        this.setupEventListeners();
        slideout.classList.add('show');
        overlay.classList.add('show');
      }
    });
  }

  /**
   * Serialize and submit the slideout detail form (block / marker) via ApiService.
   * On success the in-memory timeline is updated and the panel closes.
   *
   * @param {HTMLFormElement} form
   */
  async submitForm(form) {
    const url = form.getAttribute('action');
    if (!url) return;

    // Serialize all named controls (checkboxes -> 1/0)
    const data = {};
    form.querySelectorAll('[name]').forEach((el) => {
      if (el.type === 'checkbox') {
        data[el.name] = el.checked ? 1 : 0;
      } else if (el.type === 'radio') {
        if (el.checked) data[el.name] = el.value;
      } else {
        data[el.name] = el.value;
      }
    });

    const mode = (data.timeline_mode || this.mode || 'block');

    try {
      let result;
      if (window.ApiService) {
        const httpResult = await ApiService.post(url, data);
        result = (httpResult && httpResult.data !== undefined) ? httpResult.data : httpResult;
      } else {
        const response = await fetch(url, {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify(data)
        });
        result = await response.json();
      }

      const ok = result && (result.success === undefined || result.success === true);
      const payload = result && result.data ? result.data : result;

      if (ok) {
        if (mode === 'marker' && typeof window.updateMarker === 'function') {
          window.updateMarker(payload);
        } else if (typeof window.updateBlock === 'function') {
          window.updateBlock(payload);
        } else {
          this.hide();
        }
      } else {
        // Validation / server error: surface the message and field errors.
        const message = (result && result.message) || this._t('Unable to complete the transaction');
        if (window.NotificationManager) {
          NotificationManager.error ? NotificationManager.error(message) : NotificationManager.info(message);
        }
        const errors = (result && result.errors) || (payload && payload.errors);
        if (errors && typeof errors === 'object') {
          Object.keys(errors).forEach((field) => {
            const el = document.getElementById(field);
            if (el) el.classList.add('error');
          });
        }
      }
    } catch (error) {
      console.error('Slideout form submit failed:', error);
      if (window.NotificationManager && NotificationManager.error) {
        NotificationManager.error(this._t('Unable to complete the transaction'));
      }
    }
  }

  hide() {
    const slideout = document.getElementById('timeline-slideout');
    const overlay = document.getElementById('timeline-slideout-overlay');

    if (!slideout || !overlay) return;

    slideout.classList.remove('show');
    overlay.classList.remove('show');
  }

  switchTab(tabName) {
    const slideout = document.getElementById('timeline-slideout');
    const timelineId = document.getElementById('timeline_id');
    const project_id = document.getElementById('timeline_project_id');

    if (!slideout || !timelineId || !project_id) return;

    // Update tab buttons
    const tabs = slideout.querySelectorAll('.slideout-tab');
    tabs.forEach(tab => {
      tab.classList.toggle('active', tab.getAttribute('data-tab') === tabName);
    });

    // Update tab content
    const tabContents = slideout.querySelectorAll('.slideout-tab-content');
    tabContents.forEach(content => {
      content.classList.toggle('active', content.id === `tab-${tabName}`);
    });

    // Load content for the selected tab
    let action = 'block';
    if (tabName === 'connections') {
      action = 'connection';
    } else if (tabName === 'progress') {
      action = 'progress';
    }
    this.show({id: timelineId.value}, project_id.value, action);
  }



  confirmDeleteConnection(button) {
    if (confirm(this._t('You want to XXX ?').replace(/XXX/, this._t('delete')))) {
      if (window.timelineCalendar) {
        timelineCalendar.connectionManager.deleteConnection(button.dataset.id);
      }
      const row = button.parentNode;
      if (row && row.parentNode) row.parentNode.removeChild(row);
    }
  }

  /**
   * Show the sub-task form in the shared Now.js Modal.
   *
   * Modal scans whatever content it is given, from the constructor or from
   * setContent(), so the date, range, select and file inputs inside arrive as
   * real Now.js controls.
   *
   * @param {string} html Server rendered form markup
   */
  _showFormModal(html) {
    if (this._modal) {
      this._modal.destroy();
      this._modal = null;
    }

    this._modal = new Modal({
      title: this._t('Sub-task'),
      titleClass: 'icon-tools',
      className: 'subtask-form-modal',
      content: html
    });
    this._modal.show();

    const form = this._modal.body.querySelector('form');
    if (!form) {
      return;
    }

    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      const data = {};
      // A file input has to travel as FormData; a plain object would send the
      // fake path string and the upload would silently never happen.
      const fileInputs = Array.from(form.querySelectorAll('input[type="file"]'));
      const hasFiles = fileInputs.some((input) => input.files && input.files.length > 0);
      const body = hasFiles ? new FormData() : data;

      form.querySelectorAll('[name]').forEach((el) => {
        let value;
        if (el.type === 'file') {
          if (hasFiles && el.files) {
            Array.from(el.files).forEach((file) => body.append(el.name, file));
          }
          return;
        } else if (el.type === 'checkbox') {
          value = el.checked ? 1 : 0;
        } else if (el.type === 'radio') {
          if (!el.checked) return;
          value = el.value;
        } else {
          value = el.value;
        }
        // data doubles as the plain-object body and as the lookup used below
        data[el.name] = value;
        if (hasFiles) {
          body.append(el.name, value);
        }
      });

      try {
        let result;
        if (window.ApiService) {
          const httpResult = await ApiService.post(form.getAttribute('action'), body);
          result = (httpResult && httpResult.data !== undefined) ? httpResult.data : httpResult;
        } else {
          const res = await fetch(form.getAttribute('action'), hasFiles
            ? {method: 'POST', body: body}
            : {
              method: 'POST',
              headers: {'Content-Type': 'application/json'},
              body: JSON.stringify(data)
            });
          result = await res.json();
        }

        const ok = result && (result.success === undefined || result.success === true);
        if (!ok) {
          const msg = (result && result.message) || this._t('Unable to complete the transaction');
          if (window.NotificationManager) {
            NotificationManager.error ? NotificationManager.error(msg) : alert(msg);
          }
          return;
        }

        this._modal.hide();

        const blockId = data.timeline_id || data.block_id;
        const payload = result && result.data ? result.data : null;
        if (payload && payload.progress !== undefined && typeof window.updateBlock === 'function') {
          window.updateBlock({id: parseInt(blockId), progress: payload.progress}, false);
        }
        if (typeof window.timelineSlideout !== 'undefined') {
          timelineSlideout.switchTab('progress');
        }
        if (window.NotificationManager && NotificationManager.success) {
          NotificationManager.success(this._t('Saved successfully'));
        }
      } catch (err) {
        console.error('Modal form submit failed:', err);
      }
    });
  }


  async delete() {
    const project_id = document.getElementById('timeline_project_id');
    const id = document.getElementById('timeline_id');
    const mode = document.getElementById('timeline_mode');

    if (!project_id || !id || !mode) {
      return;
    }

    if (!confirm(this._t('You want to XXX ?').replace(/XXX/, this._t('delete')))) {
      return;
    }

    if (mode.value === 'marker') {
      if (window.timelineCalendar) timelineCalendar.markerManager.deleteMarker(id.value);
      this.hide();
    } else if (mode.value === 'block') {
      if (window.timelineCalendar) timelineCalendar.dataManager.deleteBlock(id.value);
      this.hide();
    }
  }
}

// Initialize slideout when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  window.timelineSlideout = new TimelineSlideout();
});

window.TimelineSlideout = TimelineSlideout;
